import { format, Locale } from "date-fns";
import { de, enGB } from "date-fns/locale";
import i18n from "i18next";

/**
 * Enum for date format types.
 */
export enum DateFormatType {
  SHORT = "short",
  FULLYEAR = "fullYear",
  WITHDAYNAME = "withDayName",
  VERBOSE = "verbose",
}

/**
 * Map of language codes to date-fns locales.
 */
export const languageMapForDateFns: Record<string, Locale> = {
  en: enGB,
  de,
};

interface LocaleAndFormat {
  locale: Locale;
  dateFormat: string;
}

/**
 * Retrieves the current base language code from i18n.
 */
const getCurrentLanguage = (): string => (i18n.language || "de").split("-")[0];

/**
 * Retrieves the locale and date format for the current language and format type.
 *
 * @param formatType - The type of date format (DateFormatType).
 */
const getLocaleAndDateFormat = (
  formatType: DateFormatType
): LocaleAndFormat => {
  const language = getCurrentLanguage();

  // Get date formats from i18n resources
  const dateFormats = i18n.t(`dateFormats`, { returnObjects: true }) as Record<
    string,
    string
  >;

  // Fallback to 'de' if the language is not in the map
  const fnsLocale = languageMapForDateFns[language] ?? de;

  return { locale: fnsLocale, dateFormat: dateFormats[formatType] };
};

/**
 * Formats a date into the given format based on the current language.
 *
 * @param date - The date to be formatted (as a Date object or string).
 * @param formatType - The type of date format (DateFormatType).
 */
export const formatDate = (
  date: Date | string | null,
  formatType: DateFormatType
): string => {
  if (!date) return "";
  const { locale, dateFormat } = getLocaleAndDateFormat(formatType);
  return format(new Date(date), dateFormat, { locale });
};

/**
 * Formats a date and time into a string.
 *
 * @param date - The date to be formatted (as a string).
 * @returns
 */
export function formatDateTime(date: string): string {
  const dateToConvert = new Date(parseInt(date, 10));

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  return dateToConvert.toLocaleDateString(i18n.language, options);
}
